import { useState, useRef, MouseEvent as ReactMouseEvent } from 'react';
import { throttle } from 'lodash';
import clsx from 'clsx';

import { Svgs } from 'environment';
import { CollaboratorPermissions } from 'store/data/collaborators';
import { EventType, LanguageType } from 'types/index';
import { ProjectCollaboratorAvatars } from '../ProjectCollaboratorAvatars';
import { Option } from '../ProjectsTable';

import { Dropdown } from 'components/UI/Dropdown';
import { Icon } from 'components/UI/Icons';

import { stringAsBoolean, loseFocus } from 'helpers/generic';
import { computeProjectTitle } from 'helpers/projects';

import { useProjectById, useForbidPersonalData } from 'hooks/store';
import { useOutsideClick, useEffectOnce } from 'hooks/utils';
import { useLanguage } from 'features/hooks/useLanguage';
import { useGetUserQuery } from '../../../features/data/useGetUserQuery';

const IS_PROJECT_SHARED_AVATARS_ENABLED = stringAsBoolean(
	process.env.REACT_APP_PROJECT_SHARED_AVATARS!
);

export interface ProjectCardOptions {
	onClick: (projectId: string, userAcces?: CollaboratorPermissions) => void;
	onReadMore: (description: string) => void;
}

interface Props extends ProjectCardOptions {
	projectId: string;
	projectOptions: (projectOwner?: string, userAcces?: CollaboratorPermissions) => Option[];
	isFrozen?: boolean;
	isAdminApproval?: boolean;
	isRejected?: boolean;
}

export function ProjectCard({
	projectId,
	onClick,
	onReadMore,
	projectOptions,
	isFrozen = false,
	isAdminApproval = false,
	isRejected = false
}: Props) {
	const language = useLanguage();
	const dictionary = DICTIONARY[language];

	const [menuVisible, setMenuVisible] = useState(false);
	const elementValueRef = useRef<HTMLAnchorElement>(null);

	useOutsideClick(() => setMenuVisible(false), [elementValueRef]);

	const project = useProjectById(projectId);
	const user = useGetUserQuery();
	const { forbidPersonalData } = useForbidPersonalData(projectId ?? '');

	if (!project) return null;

	function onReadMoreClick(e: ReactMouseEvent) {
		stopEvent(e);
		closeMoreMenu();
		if (project) onReadMore(project.description);
	}

	function closeMoreMenu() {
		setMenuVisible(false);
		loseFocus();
	}

	function stopEvent(e: ReactMouseEvent) {
		e.preventDefault();
		e.stopPropagation();
	}

	const titleContent = project ? computeProjectTitle(project) : '';
	const isDisabled = isFrozen || isAdminApproval || isRejected;

	return (
		<a
			ref={elementValueRef}
			onClick={() => {
				if (isDisabled) {
					// See ProjectCard for why it's implemented this way :')
					const options = projectOptions(project.createdByUser, project.userAccess);
					const editTitle = dictionary.edit;
					const viewTitle = dictionary.view;

					const editOrViewOption = options.find(
						option => option.title === editTitle || option.title === viewTitle
					);

					if (editOrViewOption) {
						editOrViewOption.onClick(projectId);
					}
					return;
				}
				onClick(projectId, project.userAccess);
			}}
			data-test-id={project.projectName.replaceAll(/[^a-zA-Z]+/g, '').toLowerCase()}
			id={project.projectName.replaceAll(/[^a-zA-Z]+/g, '').toLowerCase()}
			data-testid={'project-card_' + project.projectName}
			className={clsx(
				'group/card relative flex flex-col w-full h-[22.8rem] p-6 pt-8 rounded-md no-underline transition-all duration-200',
				isDisabled
					? 'bg-gray-100 hover:bg-gray-800 shadow-normal cursor-pointer'
					: 'cursor-pointer bg-white hover:bg-primary-500 shadow-normal hover:shadow-[0_0.6rem_1.2rem_0.6rem_rgba(37,37,58,0.15)]'
			)}
		>
			{forbidPersonalData && (
				<div className="absolute top-0 left-2 bg-gray-100 p-1">
					{' '}
					<Icon svg={Svgs.Pdna} size={s => s.s} />
				</div>
			)}
			<div className="flex justify-between mb-4">
				<span
					title={project.lastModifiedDate}
					className={clsx('text-sm text-gray-700 group-hover/card:text-white')}
				>
					{project.lastModifiedDate}
				</span>
				{!isDisabled && (
					<div className={clsx('absolute top-2 right-2')}>
						<Dropdown
							toggleComponent={({ ref, toggle }) => (
								<Icon
									ref={ref}
									variant={v => v.button}
									svg={Svgs.More}
									onClick={e => {
										stopEvent(e);
										loseFocus();
										setMenuVisible(state => !state);
										toggle();
									}}
									active={menuVisible}
									dataTestId={
										project
											? 'project-settings-dropdown_' + project.projectName
											: undefined
									}
								/>
							)}
							width={18}
							offset={{ top: 20, right: 20 }}
						>
							{projectOptions(project.createdByUser, project.userAccess).map(
								(option, index) => (
									<Dropdown.Item
										key={index}
										title={option.title}
										onClick={() => {
											closeMoreMenu();
											option.onClick(projectId);
										}}
										dataTestId={
											'project-settings-dropdown-item_' + option.title
										}
									/>
								)
							)}
						</Dropdown>
					</div>
				)}
			</div>
			<div className="flex flex-col w-full h-full">
				<h1
					title={titleContent}
					className={clsx(
						'text-base font-bold mb-2 line-clamp-3',
						!isFrozen && 'text-black group-hover/card:text-white'
					)}
				>
					{titleContent}
				</h1>
				{isFrozen && (
					<p className="text-xs text-yellow-600 mt-1">
						{user.data?.user.emailAddress.includes('ahus.no')
							? dictionary.frozenMessageAHUS
							: user.data?.user.emailAddress.includes('ous-hf.no')
							? dictionary.frozenMessageOUS
							: user.data?.user.emailAddress.includes('so-hf.no')
							? dictionary.frozenMessageSOHF
							: user.data?.user.emailAddress.includes('sshf.no')
							? dictionary.frozenMessageSSHF
							: user.data?.user.emailAddress.includes('sthf.no')
							? dictionary.frozenMessageSTHF
							: user.data?.user.emailAddress.includes('siv.no')
							? dictionary.frozenMessageSIVHF
							: user.data?.user.emailAddress.includes('sunnaas.no')
							? dictionary.frozenMessageSUNHF
							: dictionary.frozenMessage}
					</p>
				)}
				{isAdminApproval && (
					<p className="text-xs text-yellow-600 mt-1">
						{user.data?.user.emailAddress.includes('ahus.no')
							? dictionary.adminApprovalMessageAHUS
							: user.data?.user.emailAddress.includes('ous-hf.no')
							? dictionary.adminApprovalMessageOUS
							: user.data?.user.emailAddress.includes('so-hf.no')
							? dictionary.adminApprovalMessageSOHF
							: user.data?.user.emailAddress.includes('sshf.no')
							? dictionary.adminApprovalMessageSSHF
							: user.data?.user.emailAddress.includes('sthf.no')
							? dictionary.adminApprovalMessageSTHF
							: user.data?.user.emailAddress.includes('siv.no')
							? dictionary.adminApprovalMessageSIVHF
							: user.data?.user.emailAddress.includes('sunnaas.no')
							? dictionary.adminApprovalMessageSUNHF
							: dictionary.adminApprovalMessage}
					</p>
				)}
				{isRejected && (
					<p className="text-xs text-yellow-600 mt-1">
						{user.data?.user.emailAddress.includes('ahus.no')
							? dictionary.projectRejectedAHUS
							: user.data?.user.emailAddress.includes('ous-hf.no')
							? dictionary.projectRejectedOUS
							: user.data?.user.emailAddress.includes('so-hf.no')
							? dictionary.projectRejectedSOHF
							: user.data?.user.emailAddress.includes('sshf.no')
							? dictionary.projectRejectedSSHF
							: user.data?.user.emailAddress.includes('sthf.no')
							? dictionary.projectRejectedSTHF
							: user.data?.user.emailAddress.includes('siv.no')
							? dictionary.projectRejectedSIVHF
							: user.data?.user.emailAddress.includes('sunnaas.no')
							? dictionary.projectRejectedSUNHF
							: dictionary.projectRejectedMessage}
					</p>
				)}
				<ProjectDescriptionComponent
					description={project.description}
					onReadMoreClick={onReadMoreClick}
					language={language}
				/>
			</div>
			<div className={clsx('flex items-end justify-between min-h-6 mt-auto')}>
				{IS_PROJECT_SHARED_AVATARS_ENABLED && (
					<ProjectCollaboratorAvatars projectId={projectId} />
				)}
			</div>
		</a>
	);
}

interface ProjectDescriptionComponentProps {
	description: string;
	onReadMoreClick: (event: ReactMouseEvent<HTMLSpanElement>) => void;
	language: LanguageType;
}

function ProjectDescriptionComponent({
	description,
	onReadMoreClick,
	language
}: ProjectDescriptionComponentProps) {
	const descriptionRef = useRef<HTMLDivElement>(null);
	const [showReadMore, setShowReadMore] = useState(false);

	useEffectOnce(() => {
		handleReadMore();
		const debouncedHandleReadMore = throttle(handleReadMore, 100);
		window.addEventListener(EventType.Resize, debouncedHandleReadMore, true);
		return () => window.removeEventListener(EventType.Resize, debouncedHandleReadMore, true);
	});

	function handleReadMore() {
		if (!descriptionRef.current) return;
		const { clientHeight, scrollHeight } = descriptionRef.current;
		const ellipsisExists = clientHeight !== scrollHeight;
		setShowReadMore(ellipsisExists);
	}

	return (
		<>
			<div
				ref={descriptionRef}
				title={description}
				className={clsx('text-sm line-clamp-3 text-gray-700 group-hover/card:text-white')}
			>
				{description}
			</div>

			{showReadMore && (
				<div className="mt-1">
					<button
						onClick={onReadMoreClick}
						className={clsx(
							'text-sm font-medium cursor-pointer text-gray-700 group-hover/card:text-white'
						)}
					>
						{DICTIONARY[language].readMore}
					</button>
				</div>
			)}
		</>
	);
}

const DICTIONARY = {
	[LanguageType.English]: {
		frozenMessage: 'This project is frozen, please contact your administrator to unfreeze it.',
		frozenMessageAHUS:
			'This project is frozen, please contact your administrator (forskningsverktoy@ahus.no) to unfreeze it.',
		frozenMessageOUS:
			'This project is frozen, please contact your administrator (oushfpbregisterstott@ous-hf.no) to unfreeze it.',
		frozenMessageSOHF:
			'This project is frozen, please contact your administrator (forskningsavdelingen@so-hf.no) to unfreeze it.',
		frozenMessageSSHF:
			'This project is frozen, please contact your administrator (forskning@sshf.no) to unfreeze it.',
		frozenMessageSTHF:
			'This project is frozen, please contact your administrator (fou@sthf.no) to unfreeze it.',
		frozenMessageSUNHF:
			'This project is frozen, please contact your administrator (vegard.strom@sunnaas.no) to unfreeze it.',
		frozenMessageSIVHF:
			'This project is frozen, please contact your administrator (forskning@siv.no) to unfreeze it.',
		adminApprovalMessage:
			'This project is waiting for admin approval. Please contact your administrator for more information.',
		adminApprovalMessageAHUS:
			'This project is waiting for admin approval. Please contact your administrator (forskningsverktoy@ahus.no) for more information.',
		adminApprovalMessageOUS:
			'This project is waiting for admin approval. Please contact your administrator (oushfpbregisterstott@ous-hf.no) for more information.',
		adminApprovalMessageSOHF:
			'This project is waiting for admin approval. Please contact your administrator (forskningsavdelingen@so-hf.no) for more information.',
		adminApprovalMessageSSHF:
			'This project is waiting for admin approval. Please contact your administrator (forskning@sshf.no) for more information.',
		adminApprovalMessageSTHF:
			'This project is waiting for admin approval. Please contact your administrator (fou@sthf.no) for more information.',
		adminApprovalMessageSUNHF:
			'This project is waiting for admin approval. Please contact your administrator (vegard.strom@sunnaas.no) for more information.',
		adminApprovalMessageSIVHF:
			'This project is waiting for admin approval. Please contact your administrator (forskning@siv.no) for more information.',
		projectRejectedMessage:
			'This project has been rejected by the admin. Please contact your administrator for more information.',
		projectRejectedAHUS:
			'This project has been rejected by the admin. Please contact your administrator (forskningsverktoy@ahus.no) for more information.',
		projectRejectedOUS:
			'This project has been rejected by the admin. Please contact your administrator (oushfpbregisterstott@ous-hf.no) for more information.',
		projectRejectedSOHF:
			'This project has been rejected by the admin. Please contact your administrator (forskningsavdelingen@so-hf.no) for more information.',
		projectRejectedSSHF:
			'This project has been rejected by the admin. Please contact your administrator (forskning@sshf.no) for more information.',
		projectRejectedSTHF:
			'This project has been rejected by the admin. Please contact your administrator (fou@sthf.no) for more information.',
		projectRejectedSUNHF:
			'This project has been rejected by the admin. Please contact your administrator (vegard.strom@sunnaas.no) for more information.',
		projectRejectedSIVHF:
			'This project has been rejected by the admin. Please contact your administrator (forskning@siv.no) for more information.',
		edit: 'Edit project',
		view: 'View project',
		readMore: 'Read more'
	},
	[LanguageType.Norwegian]: {
		frozenMessage:
			'Dette prosjektet er fryst, vennligst kontakt din administrator for å løse det.',
		frozenMessageAHUS:
			'Dette prosjektet er fryst, vennligst kontakt din administrator (forskningsverktoy@ahus.no) for å løse det.',
		frozenMessageOUS:
			'Dette prosjektet er fryst, vennligst kontakt din administrator (oushfpbregisterstott@ous-hf.no) for å løse det.',
		frozenMessageSOHF:
			'Dette prosjektet er fryst, vennligst kontakt din administrator (forskningsavdelingen@so-hf.no) for å løse det.',
		frozenMessageSSHF:
			'Dette prosjektet er fryst, vennligst kontakt din administrator (forskning@sshf.no) for å løse det.',
		frozenMessageSTHF:
			'Dette prosjektet er fryst, vennligst kontakt din administrator (fou@sthf.no) for å løse det.',
		frozenMessageSUNHF:
			'Dette prosjektet er fryst, vennligst kontakt din administrator (vegard.strom@sunnaas.no) for å løse det.',
		frozenMessageSIVHF:
			'Dette prosjektet er fryst, vennligst kontakt din administrator (forskning@siv.no) for å løse det.',
		adminApprovalMessage:
			'Dette prosjektet venter på godkjennelse fra organisasjonsadministrator. Vennligst kontakt din administrator for mer informasjon.',
		adminApprovalMessageAHUS:
			'Dette prosjektet venter på godkjennelse fra organisasjonsadministrator. Vennligst kontakt din administrator (forskningsverktoy@ahus.no) for mer informasjon.',
		adminApprovalMessageOUS:
			'Dette prosjektet venter på godkjennelse fra organisasjonsadministrator. Vennligst kontakt din administrator (oushfpbregisterstott@ous-hf.no) for mer informasjon.',
		adminApprovalMessageSOHF:
			'Dette prosjektet venter på godkjennelse fra organisasjonsadministrator. Vennligst kontakt din administrator (forskningsavdelingen@so-hf.no) for mer informasjon.',
		adminApprovalMessageSSHF:
			'Dette prosjektet venter på godkjennelse fra organisasjonsadministrator. Vennligst kontakt din administrator (forskning@sshf.no) for mer informasjon.',
		adminApprovalMessageSTHF:
			'Dette prosjektet venter på godkjennelse fra organisasjonsadministrator. Vennligst kontakt din administrator (fou@sthf.no) for mer informasjon.',
		adminApprovalMessageSUNHF:
			'Dette prosjektet venter på godkjennelse fra organisasjonsadministrator. Vennligst kontakt din administrator (vegard.strom@sunnaas.no) for mer informasjon.',
		adminApprovalMessageSIVHF:
			'Dette prosjektet venter på godkjennelse fra organisasjonsadministrator. Vennligst kontakt din administrator (forskning@siv.no) for mer informasjon.',
		projectRejectedMessage:
			'Dette prosjektet har blitt avvist av administrator. Vennligst kontakt din administrator for mer informasjon.',
		projectRejectedAHUS:
			'Dette prosjektet har blitt avvist av administrator. Vennligst kontakt din administrator (forskningsverktoy@ahus.no) for mer informasjon.',
		projectRejectedOUS:
			'Dette prosjektet har blitt avvist av administrator. Vennligst kontakt din administrator (oushfpbregisterstott@ous-hf.no) for mer informasjon.',
		projectRejectedSOHF:
			'Dette prosjektet har blitt avvist av administrator. Vennligst kontakt din administrator (forskningsavdelingen@so-hf.no) for mer informasjon.',
		projectRejectedSSHF:
			'Dette prosjektet har blitt avvist av administrator. Vennligst kontakt din administrator (forskning@sshf.no) for mer informasjon.',
		projectRejectedSTHF:
			'Dette prosjektet har blitt avvist av administrator. Vennligst kontakt din administrator (fou@sthf.no) for mer informasjon.',
		projectRejectedSUNHF:
			'Dette prosjektet har blitt avvist av administrator. Vennligst kontakt din administrator (vegard.strom@sunnaas.no) for mer informasjon.',
		projectRejectedSIVHF:
			'Dette prosjektet har blitt avvist av administrator. Vennligst kontakt din administrator (forskning@siv.no) for mer informasjon.',
		edit: 'Rediger prosjektet',
		view: 'Se prosjektet',
		readMore: 'Les mer'
	}
};
